import React, { useEffect, useMemo, useRef } from "react";

import { CityCombobox } from "./CityCombobox";
import { LocationSelect } from "./LocationSelect";
import type { AddressFormState } from "../utils/addressForm";
import { expectedStateForCity, getCitiesForState } from "../utils/indianLocationHierarchy";
import {
  collectCityOptions,
  getDistrictOptionGroups,
  getStateOptionGroups,
} from "../utils/locationOptions";
import { lookupPincode } from "../utils/pincodeLookup";
import type { Artist, Venue } from "../types";

type Props = {
  form: AddressFormState;
  onChange: (patch: Partial<AddressFormState>) => void;
  idPrefix?: string;
  variant?: "venue" | "artist";
  venues?: Venue[];
  artists?: Artist[];
  /** Shown in district dropdown when value is not in the list (e.g. legacy edit). */
  districtOrphanValue?: string | null;
};

function cityStillValid(city: string, state: string): boolean {
  const c = city.trim();
  const s = state.trim();
  if (!c) return true;
  if (!s) return true;
  const hint = expectedStateForCity(c);
  if (hint && hint !== s) return false;
  const options = getCitiesForState(s);
  if (options.some((item) => item.toLowerCase() === c.toLowerCase())) return true;
  return !hint;
}

export const AddressFields: React.FC<Props> = ({
  form,
  onChange,
  idPrefix = "address",
  variant = "venue",
  venues = [],
  artists = [],
  districtOrphanValue,
}) => {
  const stateGroups = useMemo(() => getStateOptionGroups(), []);
  const districtGroups = useMemo(
    () => getDistrictOptionGroups(form.state, venues, artists),
    [form.state, venues, artists],
  );
  const cityOptions = useMemo(
    () => collectCityOptions(form.state, venues, artists),
    [form.state, venues, artists],
  );

  const pinLookupRef = useRef<AbortController | null>(null);
  const formRef = useRef(form);
  formRef.current = form;
  const line1Label = variant === "artist" ? "Street / area" : "Address line 1";
  const stateSelected = Boolean(form.state.trim());

  const handleStateChange = (state: string) => {
    const patch: Partial<AddressFormState> = { state };
    if (form.city.trim() && !cityStillValid(form.city, state)) {
      patch.city = "";
    }
    if (form.district.trim() && state.trim() !== form.state.trim()) {
      const districtGroups = getDistrictOptionGroups(state, venues, artists);
      const districtOptions = districtGroups.flatMap((g) => g.options);
      if (!districtOptions.some((d) => d.toLowerCase() === form.district.trim().toLowerCase())) {
        patch.district = "";
      }
    }
    onChange(patch);
  };

  const handleDistrictChange = (district: string) => {
    onChange({ district });
  };

  useEffect(() => {
    const pin = form.pincode.replace(/\D/g, "");
    if (pin.length !== 6) return;

    pinLookupRef.current?.abort();
    const controller = new AbortController();
    pinLookupRef.current = controller;

    const timer = window.setTimeout(() => {
      void lookupPincode(pin, controller.signal).then((result) => {
        if (!result || controller.signal.aborted) return;
        const current = formRef.current;
        const patch: Partial<AddressFormState> = {};
        if (!current.state.trim()) patch.state = result.state;
        if (!current.district.trim()) patch.district = result.district;
        if (Object.keys(patch).length > 0) onChange(patch);
      });
    }, 400);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [form.pincode, onChange]);

  return (
    <div className="address-fields">
      <div className="eventos-form-grid">
        <label className="eventos-field address-fields__wide">
          <span className="eventos-field-label">{line1Label}</span>
          <input
            id={`${idPrefix}-line1`}
            className="eventos-input"
            value={form.address_line1}
            onChange={(e) => onChange({ address_line1: e.target.value })}
            placeholder={variant === "artist" ? "Flat, street, locality" : "Building, street, area"}
            required
          />
        </label>
        <label className="eventos-field address-fields__wide">
          <span className="eventos-field-label">Address line 2</span>
          <input
            id={`${idPrefix}-line2`}
            className="eventos-input"
            value={form.address_line2}
            onChange={(e) => onChange({ address_line2: e.target.value })}
            placeholder="Floor, suite, block (optional)"
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">PIN code</span>
          <input
            className="eventos-input"
            value={form.pincode}
            onChange={(e) => onChange({ pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
            placeholder="6-digit PIN"
            inputMode="numeric"
            pattern="[0-9]{6}"
            title="Indian postal PIN code"
            required
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">State</span>
          <LocationSelect
            value={form.state}
            onChange={handleStateChange}
            groups={stateGroups}
            placeholder="Select state"
            required
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">District</span>
          <LocationSelect
            value={form.district}
            onChange={handleDistrictChange}
            groups={districtGroups}
            placeholder={stateSelected ? "Select district" : "Select state first"}
            disabled={!stateSelected}
            required
            orphanValue={districtOrphanValue}
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">City</span>
          <CityCombobox
            value={form.city}
            onChange={(city) => onChange({ city })}
            options={cityOptions}
            placeholder={stateSelected ? "Type or select city" : "Select state first"}
            disabled={!stateSelected}
            required
            listId={`${idPrefix}-city-list`}
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">Landmark</span>
          <input
            className="eventos-input"
            value={form.landmark}
            onChange={(e) => onChange({ landmark: e.target.value })}
            placeholder="Near metro, mall, etc."
          />
        </label>
        <label className="eventos-field">
          <span className="eventos-field-label">Country</span>
          <input
            className="eventos-input"
            value={form.country}
            onChange={(e) => onChange({ country: e.target.value })}
            required
          />
        </label>
      </div>
    </div>
  );
};
